import { useEffect } from 'react';

/**
 * Per-route document.title + meta-description + canonical + OG/Twitter
 * tags. The marketing site is a Vite SPA, so the values baked into
 * `index.html` are what crawlers see on first paint of `/`. Routes
 * other than `/` (legal docs, 404) call this hook to overwrite all
 * of those client-side so the browser tab, the share-link preview
 * re-fetched by Telegram / Twitter / Slack, and `<link rel="canonical">`
 * match the current URL.
 *
 * Cleanup restores the prior value of every tag (snapshot taken at
 * mount) so back-navigation to a route that doesn't pass the field
 * leaves the previous route's value in place rather than wiping it.
 *
 * `canonical` is a path relative to the configured origin — pass
 * `'/legal/terms'`, not the full URL.
 */
const ORIGIN = 'https://kitpod.ai';

interface MetaInput {
  title: string;
  description?: string;
  canonical?: string;
}

interface Snapshot {
  title: string;
  description: string | null;
  canonical: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogUrl: string | null;
  twitterTitle: string | null;
  twitterDescription: string | null;
}

export function useDocumentMeta({
  title,
  description,
  canonical,
}: MetaInput): void {
  useEffect(() => {
    const snapshot: Snapshot = {
      title: document.title,
      description: readMeta('name', 'description'),
      canonical: readCanonical(),
      ogTitle: readMeta('property', 'og:title'),
      ogDescription: readMeta('property', 'og:description'),
      ogUrl: readMeta('property', 'og:url'),
      twitterTitle: readMeta('name', 'twitter:title'),
      twitterDescription: readMeta('name', 'twitter:description'),
    };

    document.title = title;
    upsertMeta('property', 'og:title', title);
    upsertMeta('name', 'twitter:title', title);

    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }

    if (canonical) {
      const fullUrl = `${ORIGIN}${canonical}`;
      upsertCanonical(fullUrl);
      upsertMeta('property', 'og:url', fullUrl);
    }

    return () => {
      // Restore each tag to its pre-mount value. Nulls (== tag did
      // not exist before) trigger removal of the tag we created.
      document.title = snapshot.title;
      restoreMeta('name', 'description', snapshot.description);
      restoreMeta('property', 'og:title', snapshot.ogTitle);
      restoreMeta('property', 'og:description', snapshot.ogDescription);
      restoreMeta('property', 'og:url', snapshot.ogUrl);
      restoreMeta('name', 'twitter:title', snapshot.twitterTitle);
      restoreMeta(
        'name',
        'twitter:description',
        snapshot.twitterDescription,
      );
      restoreCanonical(snapshot.canonical);
    };
  }, [title, description, canonical]);
}

function readMeta(attr: 'name' | 'property', key: string): string | null {
  const el = document.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  return el ? el.getAttribute('content') : null;
}

function readCanonical(): string | null {
  const el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  return el ? el.getAttribute('href') : null;
}

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content: string,
): HTMLMetaElement {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function restoreMeta(
  attr: 'name' | 'property',
  key: string,
  prev: string | null,
): void {
  const el = document.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) return;
  if (prev === null) {
    el.remove();
  } else {
    el.setAttribute('content', prev);
  }
}

function upsertCanonical(href: string): HTMLLinkElement {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

function restoreCanonical(prev: string | null): void {
  const el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) return;
  if (prev === null) {
    el.remove();
  } else {
    el.setAttribute('href', prev);
  }
}
